import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Badge } from '@/components/ui/badge';
import { FIELD_MAX_LENGTHS, PASSWORD_MIN_LENGTH } from '@/lib/validation-constants';
import { cn } from '@/lib/utils';
import { UserRole } from '@/types/api';
import type { User, CreateUserRequest, UpdateUserRequest } from '@/types/api';

const ALL_ROLE_OPTIONS = [
  { value: UserRole.Owner,   label: 'Sys Admin', description: 'System-wide access across all organizations' },
  { value: UserRole.Admin,   label: 'Admin',     description: 'Full org access including user management' },
  { value: UserRole.Manager, label: 'Manager',   description: 'Can manage properties, devices, and alerts' },
  { value: UserRole.Viewer,  label: 'Viewer',    description: 'Read-only access to org data' },
];

const ROLE_RANKS: Record<UserRole, number> = {
  [UserRole.Owner]: 4,
  [UserRole.Admin]: 3,
  [UserRole.Manager]: 2,
  [UserRole.Viewer]: 1,
};

function roleOptionsForCaller(callerRole?: UserRole) {
  const callerRank = callerRole ? ROLE_RANKS[callerRole] : 0;
  return ALL_ROLE_OPTIONS.filter(o => ROLE_RANKS[o.value] <= callerRank);
}

const PASSWORD_HINT = `At least ${PASSWORD_MIN_LENGTH} characters with uppercase, lowercase, number, and special character.`;

function validatePassword(pw: string): string | undefined {
  if (!pw) return 'Password is required.';
  if (pw.length < PASSWORD_MIN_LENGTH) return `Must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (!/[A-Z]/.test(pw)) return 'Must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Must contain a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character.';
  return undefined;
}

export function UserModal({
  user,
  onClose,
  onSave,
  callerRole,
}: {
  user: User | null;
  onClose: () => void;
  onSave: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  callerRole?: UserRole;
}) {
  const isEdit = !!user;
  const isOwner = user?.role === UserRole.Owner;
  const roleOptions = roleOptionsForCaller(callerRole);

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(
    isOwner ? UserRole.Admin : (user?.role ?? UserRole.Viewer)
  );
  const [isActive, setIsActive] = useState<boolean>(user?.isActive ?? true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required.';

    if (!isEdit) {
      if (!email.trim()) {
        newErrors.email = 'Email is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = 'Enter a valid email address.';
      }

      const pwError = validatePassword(password);
      if (pwError) newErrors.password = pwError;

      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm the password.';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (isEdit) {
        const payload: UpdateUserRequest = { firstName, lastName, isActive };
        if (!isOwner) payload.role = role;
        await onSave(payload);
      } else {
        await onSave({ email, password, firstName, lastName, role } as CreateUserRequest);
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message ?? 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit User' : 'Add New User'}
      description={
        isEdit
          ? 'Update user details and role.'
          : 'Create a new user account for your organization.'
      }
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            maxLength={FIELD_MAX_LENGTHS.FIRST_NAME}
            value={firstName}
            onChange={e => {
              setFirstName(e.target.value);
              setErrors(p => ({ ...p, firstName: '' }));
            }}
            error={errors.firstName}
          />
          <Input
            label="Last Name"
            required
            maxLength={FIELD_MAX_LENGTHS.LAST_NAME}
            value={lastName}
            onChange={e => {
              setLastName(e.target.value);
              setErrors(p => ({ ...p, lastName: '' }));
            }}
            error={errors.lastName}
          />
        </div>

        {!isEdit && (
          <Input
            label="Email"
            type="email"
            required
            maxLength={FIELD_MAX_LENGTHS.EMAIL}
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setErrors(p => ({ ...p, email: '' }));
            }}
            placeholder="user@company.com"
            error={errors.email}
          />
        )}

        <div>
          <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Role {!isOwner && <span className="text-error-500 ml-1">*</span>}
          </p>
          {isOwner ? (
            <Badge variant="secondary">Sys Admin</Badge>
          ) : (
            <div className="space-y-2">
              {roleOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRole(opt.value)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors',
                    role === opt.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  <div className={cn(
                    'w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                    role === opt.value
                      ? 'border-primary-500'
                      : 'border-gray-300 dark:border-gray-600'
                  )}>
                    {role === opt.value && <div className="w-2 h-2 rounded-full bg-primary-500" />}
                  </div>
                  <div>
                    <span className={cn(
                      'text-sm font-medium',
                      role === opt.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'
                    )}>
                      {opt.label}
                    </span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{opt.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {isEdit && (
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Account active</span>
          </label>
        )}

        {!isEdit && (
          <>
            <Input
              label="Password"
              type="password"
              required
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                setErrors(p => ({ ...p, password: '' }));
              }}
              helperText={PASSWORD_HINT}
              error={errors.password}
            />
            <Input
              label="Confirm Password"
              type="password"
              required
              value={confirmPassword}
              onChange={e => {
                setConfirmPassword(e.target.value);
                setErrors(p => ({ ...p, confirmPassword: '' }));
              }}
              error={errors.confirmPassword}
            />
          </>
        )}

        {submitError && <p className="text-sm text-error-600">{submitError}</p>}

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            {isEdit ? 'Save Changes' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
